import { Container, Divider, Grid, IconButton, Typography } from '@material-ui/core';
import { createTheme, MuiThemeProvider } from '@material-ui/core/styles';
import {
    Add as AddIcon, BackspaceOutlined as BackspaceOutlinedIcon,
    Delete as DeleteIcon,
    Edit as EditIcon, Search as SearchIcon
} from '@material-ui/icons';
import MUIDataTable from "mui-datatables";
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import TextField from '../../../components/Form/TextField';
import AlertDialog from '../../../components/ui/AlertDialog';
import ButtonUI from '../../../components/ui/ButtonUI';
import LoadingUI from '../../../components/ui/LoadingUI';
import PageTitle from '../../../components/ui/PageTitleUI';
import { useAuth } from '../../../hooks/auth';
import { Filtro } from '../../../interfaces/Conference';
import ConferenceApi from '../../../services/Conference';
import getDefaultGridOptions, { CurrencyFormat, DataFormat } from '../../../utils/componentUtils';
import permissions from '../../../utils/Permissions';
import { useStyles } from './Style';
import DateTimeField from '../../../components/Form/Date'

const DebitSearch: React.FC = () => {
    const classes = useStyles();
    const [lista, setLista] = useState<any>();
    const [loading, setLoading] = useState(true);
    const { user, hasPermission } = useAuth();
    const { t } = useTranslation("DebitInsert");
    const { t: _t } = useTranslation("General");
    const history = useHistory();
    
    useEffect(() => {
        if (!hasPermission(permissions.DEBIT.Search))
            history.replace('NoPermission');
    }, [history, user])

    //Campos do Form
    interface IFormSearch {
        DataInicial?: Date | null;
        DataFinal?: Date | null;
        UsuarioAprovador?: string;
        IdPlanta?: number;
        IdEmpresa?: number;
    }

    //Metodos do form:
    const { handleSubmit, reset, control, getValues } = useForm<IFormSearch>({
        defaultValues: {
            DataInicial: null,
            DataFinal: null,
            UsuarioAprovador: '',
            IdPlanta: 0,
            IdEmpresa: 0,
        }
    });

    const columns = [
        {
            name: 'id',
            options: {
                display: false,
            }
        },
        {
            name: 'referenceDate',
            label: t("referenceDate"),
            options: {
                setCellProps: () => ({
                    align: "left"
                }),
                customBodyRender: DataFormat
            }
        },
        {
            name: 'apportionmentValue',
            label: t("apportionment"),
            options: {
                setCellProps: () => ({
                    align: "left"
                }),
                customBodyRender: CurrencyFormat
            }
        },
        {
            name: "limitationValue",
            label: t("limitation"),
            options: {
                setCellProps: () => ({
                    align: "left"
                }),
                customBodyRender: CurrencyFormat
            }
        },
        {
            name: "approverUserName1",
            label: t("approveUser1"),
            options: {
                setCellProps: () => ({
                    align: "center"
                }),
                setCellHeaderProps: () => ({
                    align: "center",
                }),
            }
        },  
        {
            name: "approverUserName2",
            label: t("approveUser2"),
            options: {
                setCellProps: () => ({
                    align: "center"
                }),
                setCellHeaderProps: () => ({
                    align: "center",
                }),
            }
        },  
        {
            name: "approverUserName3",
            label: t("approveUser3"),
            options: {
                setCellProps: () => ({
                    align: "center"
                }),
                setCellHeaderProps: () => ({
                    align: "center",
                }),
            }
        },   
        {
            name: "updatedBy",
            label: _t("updatedBy"),
            options: {
                setCellProps: () => ({
                    align: "center"
                }),
                setCellHeaderProps: () => ({
                    align: "center",
                }),
            }
        },
        {
            name: "updatedAt",
            label: _t("updatedAt"),
            options: {
                setCellProps: () => ({
                    align: "center"
                }),
                setCellHeaderProps: () => ({
                    align: "center",
                }),
                customBodyRender: DataFormat
            }
        },
        {
            name: "id",
            label: ' ',
            options: {
                setCellProps: () => ({
                    align: "center"
                }),
                disableClick: true,
                sort: false,
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => (
                    <IconButton disabled={!hasPermission(permissions.DEBIT.Edit)} size='small' edge="end" onClick={() => history.push({pathname: '/DebitInsert', state: {Id: value}}) }>
                        <EditIcon />
                    </IconButton>
                )
            }
        },  
        {
            name: "id",
            label: ' ',
            options: {
                setCellProps: () => ({
                    align: "right"
                }),
                disableClick: true,
                sort: false,
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => (
                    <AlertDialog
                        dialogTitle={_t("dialogDeleteTitle")}
                        dialogContentText={_t("dialogDeleteContentText")}
                        cancelTextButton={_t("cancelDeleteTextButton")}
                        confirmTextButton={_t("confirmDeleteTextButton")}
                        confirmDialogFunction={() => {
                            handleDeleteItem(value);
                        }}
                        Button={
                            <IconButton disabled={!hasPermission(permissions.DEBIT.Delete)} size="small" edge="end">
                                <DeleteIcon />
                            </IconButton>
                        }
                    />
                )
            }
        },     
        
    ]

    async function handleDeleteItem(id: string) {
        setLoading(true);
        await ConferenceApi.delete(id);
        let dados = getValues();
        setLoading(false);

        onSubmit(dados);
    }

    useEffect(() => {
        onSubmit({
            DataInicial: '',
            DataFinal: '',
            UsuarioAprovador: '',
            IdPlanta: user.company.plantID,
            IdEmpresa: user.company.companyID,
        });
    }, []);

    const onSubmit = async (data: any) => {
        setLoading(true);
        const filtro: Filtro = {
            DataInicial: data?.DataInicial,
            DataFinal: data?.DataFinal,
            UsuarioAprovador: data?.UsuarioAprovador,
            IdPlanta: Number(user.company.plantID),
            IdEmpresa: Number(user.company.companyID),
        };

        ConferenceApi.search(filtro)
            .then(result => {
                setLista(result);
            }).catch(() => {
                setLista([]);
            }).finally(() => {
                setLoading(false);
            });
    };

    const myTheme = createTheme({
        overrides: {
            MuiTableBody: {
                root: {
                    cursor: ''                    
                }
            },
        }
    })

    return (
        <Container maxWidth={false} component="main">
            <PageTitle title={t("debitSearchTitle")} />
            <div className={classes.rootContent}>
                <div className={classes.paper}>

                    <form translate={undefined} onSubmit={handleSubmit(onSubmit)}  >

                        <Grid container spacing={1}>
                            <Grid item xs={4} style={{ padding: "0px", display: 'flex', alignItems: 'center' }}>
                                <Typography className={classes.root}>
                                    {_t("search")}
                                </Typography>

                            </Grid>

                            <Grid item xs={8} style={{ padding: "0px", textAlign: 'right' }}>
                                <ButtonUI disabled={!hasPermission(permissions.DEBIT.Add)} color="secondary" onClick={() => history.push('/DebitInsert')} startIcon={<AddIcon />}>{_t("add")}</ButtonUI>
                                <ButtonUI onClick={() => reset()} startIcon={<BackspaceOutlinedIcon />}>{_t("clean")}</ButtonUI>
                                <ButtonUI  type="submit" color="primary" startIcon={<SearchIcon />}>{_t("search")}</ButtonUI>
                            </Grid>
                        </Grid>

                        <Divider style={{ marginTop: '10px' }} />
                        
                        <Grid container spacing={1}>

                            <Grid item xs={2}>
                                <DateTimeField
                                    label={t("initialDate")} 
                                    name="DataInicial" 
                                    control={control} 
                                    InputLabelProps={{shrink:true}}
                                    fullWidth
                                    dateAndTime /> 
                            </Grid>

                            <Grid item xs={2}>
                                <DateTimeField
                                    label={t("finalDate")} 
                                    name="DataFinal" 
                                    control={control} 
                                    InputLabelProps={{shrink:true}}
                                    fullWidth
                                    dateAndTime /> 
                            </Grid>

                            <Grid item xs={4}>
                                <TextField label={t("approveUser")} name="UsuarioAprovador" control={control} fullWidth />
                            </Grid>

                        </Grid>
                        
                    </form>
                
                    {
                        loading ? <LoadingUI /> :
                        <>
                            <Divider style={{ marginTop: '10px' }} />
                            
                            <p> {_t("totalRecords")}  {lista ? lista.length : '0'}</p>
                            
                            <MuiThemeProvider theme={myTheme}>
                                <MUIDataTable
                                    title=""
                                    data={lista}
                                    columns={columns}
                                    options={{
                                        ...getDefaultGridOptions(_t),
                                        serverSide: false,
                                        pagination: false,
                                        sort: false,
                                        selectToolbarPlacement: "none",
                                        filter: false,
                                        search: false,
                                        download: false,
                                        print: false,
                                        viewColumns: false,
                                        selectableRows: 'none',
                                        }}
                                />
                            </MuiThemeProvider>
                        </>
                    }
                </div>
            </div>
        </Container>
    )
}
export default DebitSearch;
